/** A measured figure rendered so it can be scanned down a column. */

import { cn } from "~/lib/utils";
import { EVAL_SCORE_MAX } from "~/services/evals";

interface PassRateMeterProps {
  /** Fraction in [0, 1], or null when nothing was judged. */
  rate: number | null;
  passed: number;
  judged: number;
  className?: string;
}

/** A pass rate as a value plus a meter, so the bar is never measured by eye. */
export const PassRateMeter = ({ rate, passed, judged, className }: PassRateMeterProps) => {
  if (rate == null) {
    return <span className={cn("text-xs text-muted-foreground", className)}>Not judged</span>;
  }
  const percent = Math.round(rate * 100);

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div
        className={cn(
          "h-1.5 w-16 shrink-0 overflow-hidden rounded-full",
          // A light step of the fill's ramp, not a neutral gray, so a low rate
          // reads as "mostly failing" and not "mostly empty".
          rate >= 0.9 ? "bg-green-200" : rate >= 0.7 ? "bg-orange-200" : "bg-red-200"
        )}
      >
        <div
          className={cn(
            "h-full rounded-full",
            rate >= 0.9 ? "bg-green-500" : rate >= 0.7 ? "bg-orange-500" : "bg-red-500"
          )}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-foreground">{percent}%</span>
      <span className="text-xs tabular-nums text-muted-foreground">
        {passed}/{judged}
      </span>
    </div>
  );
};

/** A 1-4 judge score, or an em dash where the run recorded none. */
export const ScoreValue = ({ score, className }: { score: number | null; className?: string }) => {
  if (score == null) {
    return <span className={cn("text-muted-foreground", className)}>—</span>;
  }
  // Thirds of the 1-4 judge scale, per the suites' judging instructions.
  const tone = score >= 3.5 ? "text-green-700" : score >= 2.5 ? "text-orange-700" : "text-red-700";
  return (
    <span className={cn("tabular-nums", tone, className)}>
      {score.toFixed(2)}
      <span className="text-muted-foreground"> / {EVAL_SCORE_MAX}</span>
    </span>
  );
};
